import React, { useState, useEffect, useRef } from 'react';
import './Registration.css';
import config from '../config';

function Registration({ onRegister, onBack }) {
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [captchaToken, setCaptchaToken] = useState('');
  const widgetIdRef = useRef(null);
  const containerRef = useRef(null);

  // Initialize Turnstile widget when component mounts
  useEffect(() => {
    if (!config.TURNSTILE_ENABLED) {
      return;
    }

    // Wait for Turnstile to be loaded
    const initTurnstile = () => {
      if (window.turnstile && containerRef.current && !widgetIdRef.current) {
        try {
          widgetIdRef.current = window.turnstile.render(containerRef.current, {
            sitekey: config.TURNSTILE_SITE_KEY,
            callback: (token) => {
              setCaptchaToken(token);
            },
            'error-callback': (errorCode) => {
              console.error('❌ Turnstile error:', errorCode);
              setError('Security verification failed. Please refresh the page and try again.');
              setCaptchaToken('');
            },
            'expired-callback': () => {
              setCaptchaToken('');
            }
          });
        } catch (err) {
          console.error('Failed to render Turnstile:', err);
        }
      }
    };

    // Try to initialize immediately
    if (window.turnstile) {
      initTurnstile();
    } else {
      // Wait for script to load
      const timer = setInterval(() => {
        if (window.turnstile) {
          initTurnstile();
          clearInterval(timer);
        }
      }, 100);

      return () => clearInterval(timer);
    }

    // Cleanup on unmount
    return () => {
      if (widgetIdRef.current && window.turnstile) {
        try {
          window.turnstile.remove(widgetIdRef.current);
        } catch (err) {
          console.error('Failed to remove Turnstile:', err);
        }
      }
    };
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!email || !emailRegex.test(email)) {
      setError('Please enter a valid email address');
      return;
    }

    if (!name || name.trim().length < 2) {
      setError('Please enter your name (at least 2 characters)');
      return;
    }

    // CAPTCHA validation (only if enabled)
    if (config.TURNSTILE_ENABLED && !captchaToken) {
      setError('Please complete the security verification');
      return;
    }

    setError('');
    setLoading(true);

    try {
      const result = await onRegister(email, name.trim(), captchaToken);

      // Only proceed if we got an explicit success response
      if (result !== true) {
        throw new Error('Registration failed. Please try again.');
      }
      // If successful (result === true), parent component will transition to verification
    } catch (err) {
      setError(err.message || 'Registration failed. Please try again.');
      setLoading(false);

      // Reset CAPTCHA on error
      if (config.TURNSTILE_ENABLED && widgetIdRef.current && window.turnstile) {
        window.turnstile.reset(widgetIdRef.current);
        setCaptchaToken('');
      }
    }
  };

  return (
    <div className="registration-container">
      <div className="registration-box">
        <h1>Create Account</h1>
        <p className="registration-subtitle">Join AvioTrack to start tracking aircraft</p>

        <form onSubmit={handleSubmit} className="registration-form">
          <div className="form-group">
            <label htmlFor="name">Full Name</label>
            <input
              id="name"
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Enter your full name"
              className="text-input"
              autoFocus
            />
          </div>

          <div className="form-group">
            <label htmlFor="email">Email Address</label>
            <input
              id="email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="Enter your email"
              className="text-input"
            />
          </div>

          {/* Turnstile CAPTCHA Widget */}
          {config.TURNSTILE_ENABLED && (
            <div className="form-group captcha-container">
              <div ref={containerRef} id="turnstile-registration-widget"></div>
            </div>
          )}

          {error && <div className="error-message">{error}</div>}

          <button type="submit" className="register-button" disabled={loading}>
            {loading ? 'Creating Account...' : 'Create Account'}
          </button>

          <button type="button" onClick={onBack} className="back-button" disabled={loading}>
            Back to Login
          </button>
        </form>
      </div>
    </div>
  );
}

export default Registration;
